import React, { useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';

import { deliveryHeadline } from '@/components/inbox/auto-reply-notice';
import { PrintImage } from '@/components/reports/print-image';
import { Text } from '@/components/ui';
import type { ExteriorApplicationDetail } from '@/services/data-service';
import { assemblePacket, isPacketPage } from '@/utils/application-packet';
import { formatBusinessDate } from '@/utils/date-utils';
import { formatDocumentSize } from '@/utils/document-download';
import { letterDeliveryAttempt } from '@/utils/message-delivery';

/**
 * The board packet: one improvement request on paper (#369).
 *
 * Everything a board needs to decide without opening the app — who asked, for what, at
 * which lot, what they sent, and the correspondence so far. Photos are printed under the
 * heading each was filed as, because "what does it look like now" and "what exactly is
 * being installed" are two different questions and a board asks them in that order.
 */

const GROUP_HEADING: Record<string, string> = {
	photo: 'Photos',
	before_photo: 'Before Photos',
	after_photo: 'After Photos',
	literature: 'Product Literature',
	quote: 'Quotes & Contracts',
	plan: 'Plans & Drawings',
	insurance: 'Insurance',
	receipt: 'Receipts',
	invoice: 'Invoices',
	misc: 'Other Files',
};

const Field: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
	if (!value) return null;
	return (
		<View style={{ flexDirection: 'row', marginBottom: 4 }}>
			<Text style={{ width: 150, fontWeight: '700', color: '#333' }}>{label}</Text>
			<Text style={{ flex: 1, color: '#111' }}>{value}</Text>
		</View>
	);
};

export interface ExteriorApplicationReportProps {
	application: ExteriorApplicationDetail;
	/** Called once every image has resolved (or failed), so the caller can print safely. */
	onImagesReady?: () => void;
}

export const ExteriorApplicationReport: React.FC<ExteriorApplicationReportProps> = ({ application, onImagesReady }) => {
	// THE BOARD PACKET (#363). Supporting documents run in the order the request type's
	// checklist specifies, because that is the order a reviewer works down. Assembled by
	// `assemblePacket` rather than inline so the rule stays testable without a renderer —
	// it was inlined-away once already, when a rewrite of this page silently stopped calling
	// these helpers and left them orphaned in the tree with nothing to fail.
	// Memoised, not `?? []` inline: a fresh array each render would defeat every memo below it.
	const requirementItems = useMemo(() => application.checklist ?? [], [application.checklist]);
	const checklist = useMemo(
		() => requirementItems.map((item) => ({ key: item.key, label: item.label })),
		[requirementItems],
	);
	// `application.attachments ?? []` is built INSIDE the callback: hoisting it would mint a
	// new array every render and defeat the memo entirely.
	const { pages, documents } = useMemo(
		() => assemblePacket(
			application.attachments ?? [],
			checklist,
			(a) => a.checklistKey,
			// MIME **or** filename extension. Two upload paths store photos as
			// `application/octet-stream`, and classifying on MIME alone silently demoted them
			// from a printed page to a bullet under "Documents" — losing them from the packet
			// the board actually reads. Shared with the board letter, which attaches the rest.
			isPacketPage,
		),
		[application.attachments, checklist],
	);

	const [settled, setSettled] = useState(0);
	useEffect(() => {
		if (settled >= pages.length) onImagesReady?.();
	}, [settled, pages.length, onImagesReady]);

	return (
		<View style={{ padding: 28 }} testID="exterior-application-report">
			<Text style={{ fontSize: 20, fontWeight: '700' }}>
				{application.label || application.requestTypeName || 'Improvement Request'}
			</Text>
			<Text style={{ color: '#555', marginBottom: 14 }}>
				{application.associationName}
				{application.applicationNumber != null ? ` · Request #${application.applicationNumber}` : ''}
			</Text>

			<Field label="Status" value={application.statusLabel} />
			<Field label="Submitter" value={application.submitterName} />
			<Field label="Email" value={application.submitterEmail} />
			<Field label="Phone" value={application.submitterPhone} />
			<Field label="Property" value={application.propertyAddress} />
			<Field
				label="Homeowner / Lot"
				value={[application.accountOwnerName, application.accountNumber].filter(Boolean).join(' · ') || null}
			/>
			<Field label="Request type" value={application.requestTypeName} />
			<Field label="Description" value={application.description} />
			<Field
				label="Review deadline"
				value={application.reviewDeadline ? new Date(application.reviewDeadline).toLocaleDateString() : null}
			/>
			<Field
				label="Board meeting"
				value={application.boardMeetingDate ? formatBusinessDate(application.boardMeetingDate) : null}
			/>
			<Field label="Decision notes" value={application.decisionNotes} />

			{/* What the request type ASKED for. Printed even when a slot has no file, because an
			    absent plot map is invisible if the packet only lists what arrived. */}
			{requirementItems.length > 0 ? (
				<View style={{ marginTop: 18 }} testID="packet-requirements">
					<Text style={{ fontSize: 14, fontWeight: '700', marginBottom: 6 }}>Checklist for this request type</Text>
					{requirementItems.map((item) => {
						// Matched on the slot KEY, not the label: labels are human text and two
						// requirements may share one, which would tick the wrong box.
						const byFile = pages.some((p) => p.slot === item.key) || documents.some((d) => d.slot === item.key);
						// `item.checked` covers a requirement a reviewer satisfied by TICKING it with
						// no file. Ignoring it made the packet contradict the app's own checklist on
						// the page whose whole purpose is to show what is outstanding.
						return (
							<Text key={item.key} style={{ color: '#111', marginBottom: 3 }}>
								{byFile || item.checked ? '☑' : '☐'} {item.label}
								{item.required === false ? ' (optional)' : ''}
							</Text>
						);
					})}
				</View>
			) : null}

			{documents.length > 0 ? (
				<View style={{ marginTop: 18 }} testID="packet-documents">
					<Text style={{ fontSize: 14, fontWeight: '700', marginBottom: 6 }}>Documents (attach separately)</Text>
					{documents.map(({ attachment: a, caption }) => (
						<Text key={a.id} style={{ color: '#111', marginBottom: 3 }}>
							• {caption ? `${caption} — ` : ''}{a.label ? `${a.label} — ` : ''}{a.fileName}
							{a.fileSize ? ` (${formatDocumentSize(a.fileSize)})` : ''}
						</Text>
					))}
				</View>
			) : null}

			{/* Packet pages: one image per sheet, in checklist order, captioned with the slot it
			    answers. One per row, full width — a board packet is read at arm's length. */}
			{pages.map(({ attachment: a, caption }) => (
				<View key={a.id} style={styles.packetPage} testID={`packet-page-${a.id}`}>
					<Text style={{ fontSize: 14, fontWeight: '700', marginBottom: 8 }}>
						{caption ?? a.label ?? GROUP_HEADING[a.attachmentType || 'misc'] ?? 'Supporting document'}
					</Text>
					<PrintImage
						attachmentId={a.id}
						caption={a.label || a.fileName}
						onSettled={() => setSettled((n) => n + 1)}
					/>
				</View>
			))}

			{application.messages?.length ? (
				<View style={{ marginTop: 18 }}>
					<Text style={{ fontSize: 14, fontWeight: '700', marginBottom: 6 }}>Correspondence</Text>
					{application.messages.map((message) => {
						// The board reads this list as "what we told the homeowner" — a letter that was
						// only recorded must not read as sent (#555).
						const delivery = Number(message.direction) === 0 ? letterDeliveryAttempt(message) : null;
						return (
							<View key={message.id} style={{ marginBottom: 8 }}>
								<Text style={{ fontWeight: '700', fontSize: 12 }}>
									{message.fromName || message.fromAddress || 'Unknown sender'}
									{message.createdAt ? ` · ${new Date(message.createdAt).toLocaleDateString()}` : ''}
								</Text>
								<Text style={{ fontSize: 12, color: '#333' }}>{message.subject || '—'}</Text>
								{delivery ? (
									<Text style={{ fontSize: 11, fontWeight: '700', color: '#8A4B00' }} testID={`packet-delivery-${message.id}`}>
										{deliveryHeadline(delivery, 'Letter')}. This copy is only a record.
									</Text>
								) : null}
							</View>
						);
					})}
				</View>
			) : null}
		</View>
	);
};

const styles = StyleSheet.create({
	packetPage: {
		marginTop: 18,
		// Each photo STARTS a sheet, heading included. `break-inside: avoid` alone is not enough:
		// the packet PDF is rendered by Browser Rendering's HeadlessChrome 128, which pushes an
		// avoided flex item to the next sheet without moving its siblings, so the next photo
		// painted on top of it. A forced break is honoured there (verified on chrome-headless-shell@128).
		...(Platform.OS === 'web' ? { breakBefore: 'page', pageBreakBefore: 'always' } : {}),
	} as ViewStyle,
});

export default ExteriorApplicationReport;
